import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useSiteConfig } from "../../contexts/SiteConfigContext";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Who Needs This", path: "/who-needs-this" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export const Header = () => {
  const { images, siteInfo, config } = useSiteConfig();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  
  // Get logo size from config, default to 5.5rem
  const logoSize = config?.general?.logoSize || "5.5rem";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0A0F]/95 backdrop-blur-2xl shadow-2xl shadow-black/30 border-b border-white/[0.04]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0 group">
          <img
            src={images.logo}
            alt={siteInfo.name}
            style={{ height: logoSize }}
            className="w-auto logo-filter transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 text-[13px] font-medium tracking-wide rounded-lg transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-[#DC2626]"
                  : "text-gray-400 hover:text-white hover:bg-white/[0.04]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA + Mobile toggle */}
        <div className="flex items-center gap-3">
          <Link to="/contact" className="hidden md:inline-flex">
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-medium px-6 h-10 text-sm tracking-wide transition-colors duration-200">
              Request a Quote
            </Button>
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0A0A0F] border-l border-white/10 w-80 p-0"
            >
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-white/[0.06]">
                  <img
                    src={images.logo}
                    alt={siteInfo.name}
                    className="h-12 w-auto logo-filter"
                  />
                </div>
                <nav className="flex flex-col p-4 gap-1 flex-1 overflow-y-auto">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`px-4 py-3 rounded-lg text-[15px] font-medium transition-colors duration-200 ${
                        location.pathname === link.path
                          ? "text-[#DC2626] bg-[#DC2626]/[0.08]"
                          : "text-gray-300 hover:text-white hover:bg-white/[0.04]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                <div className="p-6 border-t border-white/[0.06]">
                  <Link
                    to="/contact"
                    onClick={() => setMobileOpen(false)}
                  >
                    <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white w-full h-12 font-medium transition-colors duration-200">
                      Request a Quote
                    </Button>
                  </Link>
                  <div className="flex items-center gap-2 mt-4 text-gray-500">
                    <Phone className="h-4 w-4 text-[#DC2626]" />
                    <span className="text-sm">{siteInfo.phone}</span>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
